import { c as _c } from "react/compiler-runtime";
import { type Editor, Extension, type Range } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { ReactRenderer } from "@tiptap/react";
import Suggestion from "@tiptap/suggestion";
import tippy from "tippy.js";
import { type ComponentType, forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { LuBold, LuClipboardPaste, LuHeading1, LuHeading2, LuHeading3, LuItalic, LuList, LuListOrdered, LuMinus, LuQuote, LuUnderline } from "react-icons/lu";
import { resolvePopupContainer } from "@/components/email-composer/editor/popup-container";
import { Kbd } from "@/components/ui/kbd";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { pasteWithoutFormatting } from "./paste-clipboard";
export interface SlashCommandItem {
  title: string;
  description: string;
  Icon: ComponentType<{
    size?: number;
    className?: string;
  }>;
  // Keywords the user might type after "/" to reach this command.
  keywords: string[];
  run: (editor: Editor, range: Range) => void;
}
export const slashCommandPluginKey = new PluginKey("slashCommand");
const SLASH_COMMANDS: SlashCommandItem[] = [{
  title: "Bold",
  description: "Toggle bold text",
  Icon: LuBold,
  keywords: ["bold", "strong", "b"],
  run: (editor, range) => editor.chain().focus().deleteRange(range).toggleBold().run()
}, {
  title: "Italic",
  description: "Toggle italic text",
  Icon: LuItalic,
  keywords: ["italic", "emphasis", "i"],
  run: (editor, range) => editor.chain().focus().deleteRange(range).toggleItalic().run()
}, {
  title: "Underline",
  description: "Toggle underlined text",
  Icon: LuUnderline,
  keywords: ["underline", "u"],
  run: (editor, range) => editor.chain().focus().deleteRange(range).toggleUnderline().run()
}, {
  title: "Heading 1",
  description: "Large section heading",
  Icon: LuHeading1,
  keywords: ["heading", "h1", "title", "large"],
  run: (editor, range) => editor.chain().focus().deleteRange(range).toggleHeading({
    level: 1
  }).run()
}, {
  title: "Heading 2",
  description: "Medium section heading",
  Icon: LuHeading2,
  keywords: ["heading", "h2", "subtitle", "medium"],
  run: (editor, range) => editor.chain().focus().deleteRange(range).toggleHeading({
    level: 2
  }).run()
}, {
  title: "Heading 3",
  description: "Small section heading",
  Icon: LuHeading3,
  keywords: ["heading", "h3", "small"],
  run: (editor, range) => editor.chain().focus().deleteRange(range).toggleHeading({
    level: 3
  }).run()
}, {
  title: "Bulleted list",
  description: "Simple bullet list",
  Icon: LuList,
  keywords: ["bullet", "unordered", "list", "ul"],
  run: (editor, range) => {
    const chain = editor.chain().focus().deleteRange(range);
    if (!editor.isActive("bulletList")) chain.toggleBulletList();
    chain.updateAttributes("bulletList", {
      dashed: false
    }).run();
  }
}, {
  title: "Dashed list",
  description: "Bullet list with dashes",
  Icon: LuMinus,
  keywords: ["dash", "dashed", "hyphen", "list"],
  run: (editor, range) => {
    const chain = editor.chain().focus().deleteRange(range);
    if (!editor.isActive("bulletList")) chain.toggleBulletList();
    chain.updateAttributes("bulletList", {
      dashed: true
    }).run();
  }
}, {
  title: "Numbered list",
  description: "Ordered list with numbers",
  Icon: LuListOrdered,
  keywords: ["number", "ordered", "list", "ol"],
  run: (editor, range) => editor.chain().focus().deleteRange(range).toggleOrderedList().run()
}, {
  title: "Quote",
  description: "Callout / block quote",
  Icon: LuQuote,
  keywords: ["quote", "blockquote", "callout", "cite"],
  run: (editor, range) => editor.chain().focus().deleteRange(range).toggleBlockquote().run()
}, {
  title: "Paste without formatting",
  description: "Drop the clipboard's fonts and colors",
  Icon: LuClipboardPaste,
  keywords: ["paste", "plain", "unformatted", "text", "clipboard"],
  run: (editor, range) => {
    editor.chain().focus().deleteRange(range).run();
    void pasteWithoutFormatting(editor).then(pasted => {
      if (pasted) return;
      toast({
        title: "Can't read the clipboard",
        description: <>
              Your browser blocked clipboard access. Press{" "}
              <Kbd command="Shift+V" /> to paste without formatting.
            </>,
        variant: "error"
      });
    });
  }
}];
function filterSlashCommands(query: string): SlashCommandItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return SLASH_COMMANDS;
  return SLASH_COMMANDS.filter(item => item.title.toLowerCase().includes(q) || item.keywords.some(k => k.includes(q)));
}
interface SlashListRef {
  onKeyDown: (props: {
    event: KeyboardEvent;
  }) => boolean;
}
interface SlashListProps {
  items: SlashCommandItem[];
  command: (item: SlashCommandItem) => void;
}
const SlashCommandList = forwardRef<SlashListRef, SlashListProps>((t0, ref) => {
  const $ = _c(14);
  const {
    items,
    command
  } = t0;
  const [selectedIndex, setSelectedIndex] = useState(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setSelectedIndex(0);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== items) {
    t2 = [items];
    $[1] = items;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== command || $[4] !== items || $[5] !== selectedIndex) {
    t3 = () => ({
      onKeyDown: t4 => {
        const {
          event
        } = t4;
        if (items.length === 0) {
          return ["ArrowUp", "ArrowDown", "Enter"].includes(event.key);
        }
        if (event.key === "ArrowUp") {
          setSelectedIndex(prev => (prev + items.length - 1) % items.length);
          return true;
        }
        if (event.key === "ArrowDown") {
          setSelectedIndex(prev_0 => (prev_0 + 1) % items.length);
          return true;
        }
        if (event.key === "Enter") {
          const item = items[selectedIndex];
          if (item) {
            command(item);
          }
          return true;
        }
        return false;
      }
    });
    $[3] = command;
    $[4] = items;
    $[5] = selectedIndex;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  useImperativeHandle(ref, t3);
  if (items.length === 0) {
    let t4;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="rounded-lg border bg-background/95 px-3 py-2 text-body text-muted-foreground shadow-lg backdrop-blur-sm">No blocks found</div>;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  let t4;
  if ($[8] !== command || $[9] !== items || $[10] !== selectedIndex) {
    t4 = items.map((t5, index) => {
      const {
        title,
        description,
        Icon
      } = t5;
      return <button key={title} type="button" onMouseDown={_temp} onClick={() => command(items[index])} className={cx("flex items-center gap-2 rounded-md px-1.5 py-1 text-left transition-colors", index === selectedIndex ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-hover")}><span className={cx("flex h-5 w-5 shrink-0 items-center justify-center rounded border", index === selectedIndex ? "border-primary-foreground/30" : "border-border bg-background")}><Icon size={12} /></span><span className="flex flex-col"><span className="text-caption-strong leading-tight">{title}</span><span className={cx("text-caption leading-tight", index === selectedIndex ? "text-primary-foreground/80" : "text-muted-foreground")}>{description}</span></span></button>;
    });
    $[8] = command;
    $[9] = items;
    $[10] = selectedIndex;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = <div className="flex max-h-[min(320px,50vh)] w-56 flex-col overflow-auto rounded-lg border bg-background/95 p-1 shadow-lg backdrop-blur-sm">{t4}</div>;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
});
SlashCommandList.displayName = "SlashCommandList";

/**
 * Notion-style "/" block menu. Type "/" to insert headings, lists, or a quote.
 * Only added to the editor in richText mode. Reuses the same tippy + ReactRenderer
 * popup approach as the @-mention suggestion so the two coexist.
 */
export const SlashCommand = Extension.create({
  name: "slashCommand",
  addProseMirrorPlugins() {
    return [Suggestion<SlashCommandItem>({
      editor: this.editor,
      char: "/",
      pluginKey: slashCommandPluginKey,
      // Fire anywhere inside a text block. Suggestion only triggers when "/"
      // follows a space or the line start (allowedPrefixes), so "BP w/o meds"
      // never opens the menu, but "text /" mid-line does.
      allow: ({
        state,
        range
      }) => state.doc.resolve(range.from).parent.type.isTextblock,
      items: ({
        query
      }) => filterSlashCommands(query),
      command: ({
        editor,
        range,
        props
      }) => props.run(editor, range),
      render: () => {
        let component: ReactRenderer<SlashListRef>;
        let popup: ReturnType<typeof tippy>[0] | undefined;
        return {
          onStart: props => {
            component = new ReactRenderer(SlashCommandList, {
              props: {
                items: props.items,
                command: (item: SlashCommandItem) => props.command(item)
              },
              editor: props.editor
            });
            popup = tippy("body", {
              getReferenceClientRect: props.clientRect as () => DOMRect,
              appendTo: () => resolvePopupContainer(props.editor),
              content: component.element,
              showOnCreate: true,
              interactive: true,
              trigger: "manual",
              placement: "bottom-start"
            })[0];
          },
          onUpdate: props => {
            component?.updateProps({
              items: props.items,
              command: (item: SlashCommandItem) => props.command(item)
            });
            popup?.setProps({
              getReferenceClientRect: props.clientRect as () => DOMRect
            });
          },
          onKeyDown: props => {
            if (props.event.key === "Escape") {
              popup?.hide();
              return true;
            }
            return component?.ref?.onKeyDown(props) ?? false;
          },
          onExit: () => {
            popup?.destroy();
            component?.destroy();
          }
        };
      }
    })];
  }
});
function _temp(e) {
  return e.preventDefault();
}